import { useEffect, useRef, useState } from 'react';
import { browserGet } from './client';
import type { ApiClient } from '../../api/client';

type Run = { id: string; message_id: string | null; status: string; created_at: string };
type Event = { id: number; stream: string; payload: string };
type Artifact = { id: string; service_name: string; size_bytes: number; truncated: boolean };
type Message = { id: string; content: string; status: string };
type Resource = { id: string; name: string | null; status?: string };

export function ToolBrowserActivity({ conversationId, runs, logs, artifacts: artifactsAllowed = logs, apiClient, onExpired }: {
  conversationId: string; runs: boolean; logs: boolean; artifacts?: boolean; apiClient?: ApiClient; onExpired: () => Promise<void>;
}) {
  const [queue, setQueue] = useState<Message[]>([]);
  const [runRows, setRuns] = useState<Run[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [output, setOutput] = useState('');
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  const base = `${apiClient ? '/api' : '/api/browser'}/conversations/${encodeURIComponent(conversationId)}`;
  const read = apiClient?.get ?? browserGet;
  useEffect(() => () => { generation.current += 1; }, []);

  async function load(kind: 'queue' | 'runs' | 'artifacts', id?: string) {
    const request = ++generation.current;
    setOutput('');
    setStatus('Loading activity…');
    try {
      if (id) {
        if (kind === 'runs') {
          const events = await read<Event[]>(`${base}/runs/${encodeURIComponent(id)}/events`);
          if (generation.current === request) setOutput(events.map((event) => `[${event.stream}] ${event.payload}`).join('\n'));
        } else {
          const artifact = await read<{ content: string }>(`${base}/artifacts/${encodeURIComponent(id)}`);
          if (generation.current === request) setOutput(artifact.content);
        }
      } else if (kind === 'queue') {
        const rows = await read<Message[]>(`${base}/queue`);
        if (generation.current === request) setQueue(rows);
      } else if (kind === 'runs') {
        const rows = await read<Run[]>(`${base}/runs`);
        if (generation.current === request) setRuns(rows);
      } else {
        const rows = await read<Artifact[]>(`${base}/artifacts`);
        if (generation.current === request) setArtifacts(rows);
      }
      if (generation.current === request) setStatus('Activity refreshed.');
    } catch (error) {
      if (generation.current !== request) return;
      setQueue([]); setRuns([]); setArtifacts([]); setOutput('');
      setStatus(error instanceof Error ? error.message : 'Activity unavailable.');
      if (error instanceof Error && error.message === 'Browser session expired or revoked.') await onExpired();
    }
  }

  return <div className="card">
    <h2>Conversation activity</h2>
    <button type="button" onClick={() => void load('queue')}>Refresh queue</button>
    {runs && <button type="button" onClick={() => void load('runs')}>Refresh runs</button>}
    {artifactsAllowed && <button type="button" onClick={() => void load('artifacts')}>Refresh log artifacts</button>}
    <p role="status">{status}</p>
    <ul>{queue.map((message) => <li key={message.id}>{message.status}: {message.content}</li>)}</ul>
    <ul>{runRows.map((run) => <li key={run.id}>{run.status} ({run.created_at}) {logs && <button type="button" onClick={() => void load('runs', run.id)}>Read run events</button>}</li>)}</ul>
    <ul>{artifacts.map((artifact) => <li key={artifact.id}><button type="button" onClick={() => void load('artifacts', artifact.id)}>{artifact.service_name}</button> {artifact.size_bytes} bytes{artifact.truncated ? ' (truncated)' : ''}</li>)}</ul>
    {output && <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{output}</pre>}
  </div>;
}

export function ToolBrowserInfrastructure({ servers, executors, onExpired }: {
  servers: boolean; executors: boolean; onExpired: () => Promise<void>;
}) {
  const [rows, setRows] = useState<Resource[]>([]);
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);
  async function load(kind: 'servers' | 'executors') {
    const request = ++generation.current;
    setRows([]);
    try {
      const available = await browserGet<Resource[]>(`/api/browser/${kind}`);
      if (generation.current === request) { setRows(available); setStatus(`Authorized ${kind}`); }
    } catch (error) {
      if (generation.current !== request) return;
      setStatus(error instanceof Error ? error.message : 'Resource unavailable.');
      if (error instanceof Error && error.message === 'Browser session expired or revoked.') await onExpired();
    }
  }
  if (!servers && !executors) return null;
  return <div className="card">
    <h2>Infrastructure</h2>
    {servers && <button type="button" onClick={() => void load('servers')}>View authorized servers</button>}
    {executors && <button type="button" onClick={() => void load('executors')}>View authorized executors</button>}
    <p role="status">{status}</p>
    <ul>{rows.map((row) => <li key={row.id}>{row.name ?? row.id}{row.status ? `: ${row.status}` : ''}</li>)}</ul>
  </div>;
}
